import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { envConfig } from "../utils/envConfig";
import { getAuthToken, clearAuthData } from "../context/AuthActions";
import { KeyedRateLimiter } from "../utils/rateLimiter";

export interface ApiResponse<T = unknown> {
  status?: boolean;
  success?: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
}

export class ApiError extends Error {
  statusCode?: number;
  response?: ApiResponse;

  constructor(message: string, statusCode?: number, response?: ApiResponse) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.response = response;
  }
}

const api: AxiosInstance = axios.create({
  baseURL: envConfig.apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

const PUBLIC_PATH_PREFIXES = [
  "/api/v2/auth/sign-in",
  "/api/v2/auth/sign-up",
  "/api/v2/auth/forgot-password",
];

const LOCAL_SECURITY_CHALLENGE_PATHS = [
  "/api/v2/user/verify-pin",
  "/api/v2/settings/security/2fa/verify",
];

function matchesPath(requestUrl: string, paths: readonly string[]): boolean {
  let pathname = requestUrl;

  try {
    pathname = new URL(requestUrl, envConfig.apiUrl).pathname;
  } catch {
    [pathname] = requestUrl.split(/[?#]/, 1);
  }

  return paths.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

const apiRateLimiter = new KeyedRateLimiter({ maxCalls: 50, windowMs: 10_000 });

function getRequestKey(config: { method?: string; url?: string }): string {
  const method = String(config.method || "get").toLowerCase();
  let pathname = config.url || "";
  try {
    pathname = new URL(config.url || "", envConfig.apiUrl).pathname;
  } catch {
    [pathname] = (config.url || "").split(/[?#]/, 1);
  }
  return `${method} ${pathname}`;
}

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!apiRateLimiter.tryAcquire(getRequestKey(config))) {
      throw new ApiError(
        "Too many requests. Please slow down and try again.",
        429
      );
    }

    const token = getAuthToken();

    if (token) {
      if (
        config.headers &&
        typeof (config.headers as { set?: unknown }).set === "function"
      ) {
        (config.headers as { set: (k: string, v: string) => void }).set(
          "Authorization",
          `Bearer ${token}`
        );
      } else {
        (config.headers as Record<string, string>).Authorization =
          `Bearer ${token}`;
      }
    } else if (
      process.env.NODE_ENV !== "production" &&
      config.url &&
      !PUBLIC_PATH_PREFIXES.some((prefix) => config.url!.startsWith(prefix))
    ) {
      console.warn(
        `[apiService] ${config.method?.toUpperCase()} ${config.url} sent without an auth token`
      );
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error instanceof ApiError) {
      return Promise.reject(error);
    }

    if (!error.response) {
      return Promise.reject(new ApiError(error.message || "Network error"));
    }

    const { status, data, config } = error.response;

    const serverMessage =
      data?.message || data?.error || error.message || "Request failed";

    if (status === 401) {
      const requestUrl: string = config?.url || "";
      const isPublicAuthRequest = matchesPath(requestUrl, PUBLIC_PATH_PREFIXES);
      const isLocalSecurityChallenge = matchesPath(
        requestUrl,
        LOCAL_SECURITY_CHALLENGE_PATHS
      );

      if (!isPublicAuthRequest && !isLocalSecurityChallenge) {
        clearAuthData();
        window.location.href = "/login";
        return Promise.reject(
          new ApiError("Unauthorized – session expired", 401, data)
        );
      }
    }

    return Promise.reject(new ApiError(serverMessage, status, data));
  }
);

function unwrap<T>(response: AxiosResponse<ApiResponse<T>>): T {
  const body = response.data;

  if (body === undefined || body === null) {
    return undefined as unknown as T;
  }

  if (
    typeof body !== "object" ||
    (!("status" in body) && !("success" in body))
  ) {
    return body as unknown as T;
  }

  const isSuccessful =
    typeof body.success === "boolean"
      ? body.success
      : typeof body.status === "boolean"
        ? body.status
        : true;

  if (!isSuccessful) {
    throw new ApiError(body.message || "Request failed", response.status, body);
  }

  return body.data as T;
}

function passthrough<T>(response: AxiosResponse<T>): AxiosResponse<T> {
  return response;
}

function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) {
    return "";
  }

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
}

const inFlightGetRequests = new Map<string, Promise<unknown>>();

export async function get<T = unknown>(
  url: string,
  params?: Record<string, unknown>
): Promise<T> {
  const queryString = buildQueryString(params);
  const sessionToken = getAuthToken() ?? "anonymous";
  const requestKey = `${sessionToken}:${url}${queryString}`;

  const existing = inFlightGetRequests.get(requestKey);
  if (existing) {
    return existing as Promise<T>;
  }

  const pending = (async () => {
    const response = await api.get<ApiResponse<T>>(url + queryString);
    return unwrap(response);
  })().finally(() => {
    if (inFlightGetRequests.get(requestKey) === pending) {
      inFlightGetRequests.delete(requestKey);
    }
  });

  inFlightGetRequests.set(requestKey, pending);
  return pending as Promise<T>;
}

export const __resetApiServiceGetCacheForTests = (): void => {
  inFlightGetRequests.clear();
};

export async function getQuery<T = unknown>(
  url: string,
  params?: Record<string, unknown>,
  config: AxiosRequestConfig = {}
): Promise<T> {
  const response = await api.get<ApiResponse<T>>(url, { ...config, params });
  return unwrap(response);
}

export async function getFile(
  url: string,
  params?: Record<string, unknown>
): Promise<AxiosResponse<Blob>> {
  const queryString = buildQueryString(params);
  const response = await api.get<Blob>(url + queryString, {
    responseType: "blob",
  });
  return passthrough(response);
}

export async function create<T = unknown, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.post<ApiResponse<T>>(url, data, config);
  return unwrap(response);
}

export async function update<T = unknown, D = unknown>(
  url: string,
  params: Record<string, unknown> = {},
  data: D | Record<string, never> = {} as Record<string, never>,
  config: AxiosRequestConfig = {}
): Promise<T> {
  const response = await api.put<ApiResponse<T>>(url, data, {
    ...config,
    params,
  });
  return unwrap(response);
}

export async function updatePatch<T = unknown, D = unknown>(
  url: string,
  params: Record<string, unknown> = {},
  data: D | Record<string, never> = {} as Record<string, never>,
  config: AxiosRequestConfig = {}
): Promise<T> {
  const response = await api.patch<ApiResponse<T>>(url, data, {
    ...config,
    params,
  });
  return unwrap(response);
}

export async function deleteResource<T = unknown, D = unknown>(
  url: string,
  params: Record<string, unknown> = {},
  data: D | Record<string, never> = {} as Record<string, never>,
  config: AxiosRequestConfig = {}
): Promise<T> {
  const response = await api.delete<ApiResponse<T>>(url, {
    ...config,
    params,
    data,
  });
  return unwrap(response);
}

export async function createWithFile<T = unknown>(
  url: string,
  data: FormData | Record<string, unknown>,
  _files: File[] = []
): Promise<T> {
  const response = await api.post<ApiResponse<T>>(url, data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return unwrap(response);
}

export async function updateWithFile<T = unknown>(
  url: string,
  data: Record<string, unknown>,
  files: File[] = []
): Promise<T> {
  const formData = new FormData();
  formData.append("data", JSON.stringify(data));

  files.forEach((file) => {
    formData.append("images", file);
  });

  const response = await api.patch<ApiResponse<T>>(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return unwrap(response);
}

export async function addImages<T = unknown>(
  url: string,
  images: File[]
): Promise<T> {
  if (!Array.isArray(images) || images.length === 0) {
    throw new ApiError("images must be a non-empty array of files.");
  }

  const formData = new FormData();
  images.forEach((image) => {
    formData.append("images", image);
  });

  const response = await api.post<ApiResponse<T>>(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return unwrap(response);
}

export const setAuthorization = (_token?: string | null): void => {};

export const axiosInstance = api;

export default api;
